import React from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";

const candidates = [
  { name: "Rahul S.", role: "Backend Engineer", reqs: ["Startup background", "Tier 1 college"] },
  { name: "Priya M.", role: "Product Designer", reqs: ["B2B SaaS", "Available immediately"] },
  { name: "Amit K.", role: "Frontend Dev", reqs: ["React expert", "4+ years exp"] },
  { name: "Neha R.", role: "Data Scientist", reqs: ["NLP experience", "Python"] },
  { name: "Vikram T.", role: "Engineering Mgr", reqs: ["Led teams of 10+", "Fintech"] },
];

export const AiAgentIntegration = () => {
  const outerRadius = 340; // Distance 2 units
  const innerRadius = 220; // Distance 1 unit
  const seqDuration = 4.5; // Total intro sequence duration (sped up)

  return (
    <div className="relative w-full h-[800px] flex items-center justify-center overflow-hidden font-sans" style={{ background: "linear-gradient(135deg, #0B0B0F 0%, #1A1A2E 100%)" }}>
      
      {/* Central Glow */}
      <motion.div 
        className="absolute w-[400px] h-[400px] bg-[#5851D3] rounded-full blur-[120px]" 
        initial={{ opacity: 0, scale: 0.5 }}
        animate={{ opacity: 0.2, scale: 1 }}
        transition={{ duration: 1.5, ease: "easeOut" }}
      />

      {/* Rotating System */}
      <motion.div 
        className="relative flex items-center justify-center w-[800px] h-[800px]"
        initial={{ rotate: 0 }}
        animate={{ rotate: [0, 360, 360 * 21] }}
        transition={{ 
          duration: 165, // 5s for first spin, 160s for next 20 spins (8s per spin)
          times: [0, 5/165, 1],
          ease: [[0.5, 0, 0.8, 1], "linear"],
          delay: seqDuration 
        }}
      >
        
        {/* Connecting Lines */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none">
          {candidates.map((_, i) => {
            const angle = (i * 360) / candidates.length;
            const angleRad = (angle * Math.PI) / 180;
            
            const outerX = outerRadius * Math.cos(angleRad);
            const outerY = outerRadius * Math.sin(angleRad);
            const innerX = innerRadius * Math.cos(angleRad);
            const innerY = innerRadius * Math.sin(angleRad);

            const t_enter = 0.5 + i * 0.4; // Cards enter faster and closer together
            const t_arrive = t_enter + 0.6;
            const t_pull = 3.2; // Start pulling in sooner

            const times = [
              0,
              t_enter / seqDuration,
              t_arrive / seqDuration,
              t_pull / seqDuration,
              1
            ];

            return (
              <motion.line 
                key={`line-${i}`}
                x1="400" 
                y1="400" 
                stroke="#5851D3" 
                strokeWidth="1.5" 
                strokeDasharray="4 4" 
                initial={{ x2: 400, y2: 400, opacity: 0 }}
                animate={{ 
                  x2: [400, 400, 400 + outerX, 400 + outerX, 400 + innerX],
                  y2: [400, 400, 400 + outerY, 400 + outerY, 400 + innerY],
                  opacity: [0, 0, 0.3, 0.3, 0.3]
                }}
                transition={{ 
                  duration: seqDuration, 
                  times: times,
                  ease: ["linear", "easeOut", "linear", "easeInOut"]
                }}
              />
            );
          })}
        </svg>

        {/* Orbiting Cards */}
        {candidates.map((candidate, i) => {
          const angle = (i * 360) / candidates.length;
          const angleRad = (angle * Math.PI) / 180;
          
          const outerX = outerRadius * Math.cos(angleRad);
          const outerY = outerRadius * Math.sin(angleRad);
          const innerX = innerRadius * Math.cos(angleRad);
          const innerY = innerRadius * Math.sin(angleRad);
          
          const startX = outerX * 2.5;
          const startY = outerY * 2.5;

          const t_enter = 0.5 + i * 0.4;
          const t_arrive = t_enter + 0.6;
          const t_pull = 3.2;

          const times = [
            0,
            t_enter / seqDuration,
            t_arrive / seqDuration,
            t_pull / seqDuration,
            1
          ];

          return (
            <motion.div
              key={`card-container-${i}`}
              className="absolute"
              initial={{ x: startX, y: startY, opacity: 0, scale: 0.5 }}
              animate={{ 
                x: [startX, startX, outerX, outerX, innerX],
                y: [startY, startY, outerY, outerY, innerY],
                opacity: [0, 0, 1, 1, 1],
                scale: [0.5, 0.5, 1, 1, 1]
              }}
              transition={{ 
                duration: seqDuration, 
                times: times,
                ease: ["linear", "easeOut", "linear", "easeInOut"]
              }}
            >
              {/* Counter-rotation to keep cards upright */}
              <motion.div
                initial={{ rotate: 0 }}
                animate={{ rotate: [0, -360, -360 * 21] }}
                transition={{ 
                  duration: 165, 
                  times: [0, 5/165, 1],
                  ease: [[0.5, 0, 0.8, 1], "linear"],
                  delay: seqDuration 
                }}
                className="flex items-center justify-center"
              >
                <CandidateCard candidate={candidate} index={i} seqDuration={seqDuration} />
              </motion.div>
            </motion.div>
          );
        })}

      </motion.div>

      {/* Central AI Icon */}
      <motion.div 
        className="absolute z-10 flex items-center justify-center w-24 h-24 bg-white/5 backdrop-blur-md rounded-2xl border border-white/10 shadow-[0_0_40px_rgba(88,81,211,0.3)]"
        initial={{ scale: 0, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.8, type: "spring", bounce: 0.4 }}
      >
        <img src="/claude-icon.png" alt="AI Agent" className="w-14 h-14 object-contain" />
      </motion.div>

    </div>
  );
};

const CandidateCard = ({ candidate, index, seqDuration }: { candidate: any, index: number, seqDuration: number }) => {
  return (
    <motion.div 
      className="relative w-[260px] bg-[#F0F1F7] rounded-2xl p-4 shadow-xl border border-[#CACDD8] flex flex-col gap-3"
      initial={{ boxShadow: "0px 10px 30px rgba(0,0,0,0.1)" }}
      animate={{ 
        scale: [1, 1.05, 1],
        boxShadow: [
          "0px 10px 30px rgba(0,0,0,0.1)",
          "0px 0px 20px rgba(88,81,211,0.4)",
          "0px 10px 30px rgba(0,0,0,0.1)"
        ]
      }}
      transition={{ 
        duration: 3, 
        repeat: Infinity, 
        delay: seqDuration + index * 1.5, // Start highlights faster
        ease: "easeInOut",
        times: [0, 0.5, 1],
        repeatDelay: 6
      }}
    >
      {/* Top right logo */}
      <img src="/cutshort-logo-small.png" alt="Cutshort" className="absolute top-4 right-4 w-5 h-5 object-contain opacity-80" />
      
      {/* Header: Avatar + Info */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#5851D3] to-[#858DA0] flex items-center justify-center text-white font-semibold text-sm shadow-inner">
          {candidate.name.charAt(0)}
        </div>
        <div className="flex flex-col">
          <span className="text-[#262626] font-semibold text-sm leading-tight">{candidate.name}</span>
          <span className="text-[#858DA0] text-xs font-medium">{candidate.role}</span>
        </div>
      </div>

      {/* Requirements */}
      <div className="flex flex-col gap-1.5 mt-1">
        {candidate.reqs.map((req: string, i: number) => (
          <div key={i} className="flex items-center gap-1.5 bg-white rounded-md px-2 py-1 border border-[#CACDD8]/50">
            <CheckCircle2 className="w-3.5 h-3.5 text-[#10B981]" />
            <span className="text-[#262626] text-[11px] font-medium">{req}</span>
          </div>
        ))}
      </div>
    </motion.div>
  );
};
